/* eslint-disable no-unused-expressions */
/* eslint-disable no-unused-vars */
import React, { Children, useContext, useEffect, useState } from 'react';
import { ActionItemView, IdeaRow, Title } from '../../components';
import S, { IdeaContainer } from './style';
import { GlobalContext } from '../../context/GlobalState';
import { getUsersById } from '../../services/users';
import { updateRetrospective } from '../../services/retrospectives';
import {
  getItemById,
  getItemsByIds,
  getItemTypes,
  openItemDialog,
} from '../../services/items';
import { logEvent, TrackingEvents } from '../../tracking';
import { ActionItemType } from '../../DomainEnums';
import TeamMoodRow from '../../components/TeamMoodRow';
import { useGetItemTypes } from '../DiscussBoard/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { HostPlatform } from 'src/config/hostPlatform';

const getIcon = (workItemTypes, platformItem) =>
  workItemTypes?.find(
    (type) => type.name === platformItem.type || type.id === platformItem.type
  );

const transformCard = (workItemTypes, platformItems, allActionItems) => {
  return allActionItems
    .map((actionItem) => {
      if (actionItem.type === ActionItemType.TEXT_NOTE) {
        return actionItem;
      }

      const platformItem = platformItems.find(
        (item) => item.id === actionItem.platformActionItemId
      );

      return {
        ...actionItem,
        id: actionItem.platformActionItemId ?? null,
        text:
          platformItem?.name ||
          `Item not found: it may have been deleted or you don't have permission to access it`,
        key: platformItem.key,
        icon: (platformItem && getIcon(workItemTypes, platformItem)) ?? null,
        disabled: Boolean(!platformItem),
      };
    })
    .filter(Boolean);
};

function SummaryBoard() {
  const { retrospective, cards, user, teamMood } = useContext(GlobalContext);
  const [users, setUsers] = useState();
  const [reload, setReload] = useState(false);
  const [sortedCards, setSortedCards] = useState('');
  const [items, setItems] = useState([]);
  const [workItemsModified, setWorkItemsModified] = useState([]);
  const [facilitatorNotes, setFacilitatorNotes] = useState(
    retrospective.notes || ''
  );
  const [workItemTypes, setWorkItemTypes] = useState([]);
  const queryClient = useQueryClient();

  const isFacilitator = () => user.id === retrospective.facilitator;
  const [emotionsArray, setEmotionsArray] = useState([]);
  const { data: itemTypes, isLoading: isLoadingItemTypes } = useGetItemTypes();

  useEffect(() => {
    (async () => {
      const participants = await getUsersById([...retrospective.participants]);
      setUsers(participants);
    })();
  }, [retrospective.participants]);

  useEffect(() => {
    if (retrospective.teamMoodQuestions?.length && teamMood.length > 0) {
      const emotions = retrospective.teamMoodQuestions.map((q) => ({
        question: q,
        participantEmotions: teamMood.flatMap((p) =>
          p.questions.filter((pQ) => pQ.question === q).map((pQ) => pQ.emotion)
        ),
      }));
      setEmotionsArray(emotions);
    }
  }, [teamMood, retrospective.teamMoodQuestions]);

  useEffect(() => {
    if (cards.length === 0 || isLoadingItemTypes) return;
    (async () => {
      const actionItemIds = cards.flatMap((card) =>
        card.actionItems.map((actionItem) => actionItem?.platformActionItemId)
      );
      const ids = actionItemIds.filter((x) => x !== undefined);
      const platformItems = await getItemsByIds(ids);

      const actionsItems = cards.flatMap((card) =>
        card.actionItems.map((actionItem) => ({
          ...actionItem,
          id: actionItem.platformActionItemId,
        }))
      );
      setItems(actionsItems);

      const aux = cards.sort((card1, card2) => {
        if (card1.votes.length === card2.votes.length)
          return card1.rank - card2.rank;
        return card1.votes.length > card2.votes.length ? -1 : 1;
      });

      setWorkItemTypes(itemTypes);
      setSortedCards(aux);

      const newCards =
        actionsItems.length > 0
          ? transformCard(itemTypes, platformItems, actionsItems)
          : [];
      setWorkItemsModified(newCards);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cards,
    retrospective?.boardId,
    items?.length,
    workItemTypes?.length,
    reload,
    isLoadingItemTypes,
  ]);

  if (!retrospective || cards.length === 0 || !users || !items) {
    // TODO add loading indicator
    return;
  }

  const handleItemClick = async (itemId) => {
    logEvent(TrackingEvents.CLICK_RETROSPECTIVE_ACTION, {
      stage: retrospective.stage,
    });
    await openItemDialog(itemId);
    const editedItem = await getItemById(itemId);
    setItems(
      items.map((item) =>
        item.platformActionItemId === editedItem.id ? editedItem : item
      )
    );
    queryClient.invalidateQueries('getItemTypes').then(() => {
      setReload(!reload);
    });
    setReload(true);
  };

  const handleNotes = ({ target }) => {
    setFacilitatorNotes(target.value);
    updateRetrospective(retrospective.id, { notes: target.value });
  };

  return (
    <S.Container>
      <Title center size="lg">
        Retrospective summary
      </Title>
      <S.SectionSummary>
        <S.CardSummary>
          <S.SummaryNumber>{cards.length}</S.SummaryNumber>
          <S.SummaryLabel>
            {cards.length === 1 ? 'Idea' : 'Ideas'}
          </S.SummaryLabel>
        </S.CardSummary>
        <S.CardSummary>
          <S.SummaryNumber>{retrospective.votesCount}</S.SummaryNumber>
          <S.SummaryLabel>
            {retrospective.votesCount === 1 ? 'Vote' : 'Votes'}
          </S.SummaryLabel>
        </S.CardSummary>
        <S.CardSummary>
          <S.SummaryNumber>{retrospective.participants.length}</S.SummaryNumber>
          <S.SummaryLabel>Participants</S.SummaryLabel>
        </S.CardSummary>
        <S.CardSummary>
          <S.SummaryNumber>{retrospective.actionItemsCount}</S.SummaryNumber>
          <S.SummaryLabel>
            {retrospective.actionItemsCount === 1
              ? 'Action item'
              : 'Action items'}
          </S.SummaryLabel>
        </S.CardSummary>
      </S.SectionSummary>
      <>
        {(isFacilitator() || retrospective.notes) && (
          <Title center>Notes</Title>
        )}

        {/* eslint-disable-next-line no-nested-ternary */}
        {isFacilitator() ? (
          <S.ContainerTextField>
            <S.TextField
              placeholder="Add notes"
              value={facilitatorNotes}
              onChange={handleNotes}
            />
          </S.ContainerTextField>
        ) : retrospective.notes ? (
          <S.ContainerTextField>
            <S.Notes>{retrospective.notes}</S.Notes>
          </S.ContainerTextField>
        ) : null}
      </>
      <Title center>Participants</Title>
      <S.SectionMembers>
        {users.map((userItem) => (
          <S.PhotoMember key={userItem.id} src={userItem.photo_thumb} />
        ))}
      </S.SectionMembers>
      {(teamMood && emotionsArray.length > 0) ||
      retrospective.teamMoodQuestions?.length > 0 ? (
        <>
          <Title center>Team Mood</Title>
          <S.TeamMood>
            {retrospective.teamMoodQuestions.map((q, index) => (
              <TeamMoodRow
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                emotionsArray={emotionsArray}
                moods={teamMood}
                question={q}
                index={index}
                isSummary
              />
            ))}
          </S.TeamMood>
        </>
      ) : null}
      {workItemsModified.length > 0 && (
        <>
          <Title center>Action Items</Title>
          <S.CardActionItems>
            {Children.toArray(
              workItemsModified.map((item) => {
                return (
                  <ActionItemView
                    key={item.id + item.text}
                    id={HostPlatform.isJira() ? item.key : item.id}
                    item={item}
                    disabled={item.type === 'TEXT_NOTE'}
                    allowEdit={item.type !== 'TEXT_NOTE'}
                    allowRemove={false}
                    handleClick={() => handleItemClick(item.id)}
                  />
                );
              })
            )}
          </S.CardActionItems>
        </>
      )}
      {sortedCards.length > 0 && (
        <>
          <Title center>Ideas</Title>
          <IdeaContainer>
            {sortedCards.map((card) => (
              <IdeaRow key={card.id} idea={card} />
            ))}
          </IdeaContainer>
        </>
      )}
    </S.Container>
  );
}

export default SummaryBoard;
